import { API as MOCK_AMPLIFY_API } from '../../__mocks__/aws-amplify';
import { API as AMPLIFY_API } from 'aws-amplify';
import {
  createSampleBatchMutation,
  createCryoboxMutation,
  updateCryoboxMutation,
  linkRGCBarcodeMutation,
  requestSampleExport,
  createStagedShipmentMutation,
  addCryoboxToStagingMutation,
  removeCryoboxFromStagingMutation,
  resetStagedShipmentMutation,
  moveStagedAliquotMutation,
  manifestStagedShipmentMutation,
  finaliseShipmentMutation,
  shipmentConfirmQCMutation,
} from '../graphql/mutations';
import {
  findCryoboxesQuery,
  findSampleBatchesQuery,
  getSampleBatchQuery,
  getCryoboxQuery,
  listFreezersQuery,
  getSampleExportStatusQuery,
  listAliquotLocationsQuery,
  getStagedShipmentMetaQuery,
  getStagedShipmentQuery,
  getRankedCryoboxesQuery,
  getStagingPickListQuery,
  listShipmentsQuery,
  getShipmentManifestQuery,
  getShipmentQuery,
} from '../graphql/queries';
import {
  AddCryoboxToStagingInput,
  CreateCryoBoxInput,
  CreateSampleBatchInput,
  CreateStagedShipmentInput,
  FinaliseShipmentInput,
  LinkRGCBarcodeInput,
  MoveStagedAliquotInput,
  ReportExportStatus,
  RequestSampleResponse,
  SampleExportStatusResponse,
  UpdateCryoBoxInput,
} from './lab.types';

const API = process.env.REACT_APP_USE_MOCKS === 'true' ? MOCK_AMPLIFY_API : AMPLIFY_API;

const participantApi = {
  getSampleBatch: async (batchID: string): Promise<any> => {
    try {
      return await API.graphql({
        query: getSampleBatchQuery,
        variables: {
          batchID,
        },
      });
    } catch (error) {
      return error;
    }
  },
  findSampleBatches: async (searchTerm: string): Promise<any> => {
    try {
      return await API.graphql({
        query: findSampleBatchesQuery,
        variables: {
          searchTerm,
        },
      });
    } catch (error) {
      return error;
    }
  },
  createSampleBatch: async (inputs: CreateSampleBatchInput): Promise<any> => {
    try {
      return await API.graphql({
        query: createSampleBatchMutation,
        variables: {
          input: inputs,
        },
      });
    } catch (error) {
      return error;
    }
  },
  getCryobox: async (cryoboxUuid: string): Promise<any> => {
    try {
      return await API.graphql({
        query: getCryoboxQuery,
        variables: {
          cryoboxUuid,
        },
      });
    } catch (error) {
      return error;
    }
  },
  findCryoboxes: async (
    searchTerm: string,
    freezerId: string,
    bioBanked: string,
    offset?: number,
    limit?: number,
  ): Promise<any> => {
    try {
      return await API.graphql({
        query: findCryoboxesQuery,
        variables: {
          bioBanked: bioBanked === 'true',
          freezerId,
          searchTerm,
          offset,
          limit,
        },
      });
    } catch (error) {
      return error;
    }
  },
  createCryoBox: async (values: CreateCryoBoxInput): Promise<any> => {
    try {
      return await API.graphql({
        query: createCryoboxMutation,
        variables: {
          input: {
            serialNo: values.serialNo,
            cryoboxBarcode: values.cryoboxBarcode || undefined,
            height: parseInt(values.height),
            width: parseInt(values.width),
            sampleType: values.sampleType,
            aliquotPositions: values.aliquotPositions,
            bioBanked: false,
            freezerId: values.freezerId,
            freezerShelfNo: values.freezerShelfNo,
            shelfRackNo: values.shelfRackNo,
            rackPositionNo: values.rackPositionNo,
          },
        },
      });
    } catch (error) {
      return error;
    }
  },
  updateCryobox: async (values: UpdateCryoBoxInput): Promise<any> => {
    try {
      return await API.graphql({
        query: updateCryoboxMutation,
        variables: {
          input: {
            cryoboxUuid: values.cryoboxUuid,
            aliquotPositions: values.aliquotPositions,
            cryoboxBarcode: values.cryoboxBarcode || undefined,
            freezerId: values.freezerId,
            freezerShelfNo: values.freezerShelfNo,
            shelfRackNo: values.shelfRackNo,
            rackPositionNo: values.rackPositionNo,
            bioBanked: values.bioBanked,
          },
        },
      });
    } catch (error) {
      return error;
    }
  },
  listFreezers: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: listFreezersQuery,
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },
  linkRGCBarcode: async (inputs: LinkRGCBarcodeInput): Promise<any> => {
    try {
      return await API.graphql({
        query: linkRGCBarcodeMutation,
        variables: {
          input: inputs,
        },
      });
    } catch (error) {
      return error;
    }
  },
  getSampleExportStatus: async (): Promise<any> => {
    try {
      return (await API.graphql({
        query: getSampleExportStatusQuery,
      })) as SampleExportStatusResponse;
    } catch (error) {
      return error as SampleExportStatusResponse;
    }
  },
  requestSampleExport: async (exportType: ReportExportStatus): Promise<any> => {
    try {
      return (await API.graphql({
        query: requestSampleExport,
        variables: {
          exportType: exportType,
        },
      })) as RequestSampleResponse;
    } catch (error) {
      return error as RequestSampleResponse;
    }
  },
  createStagedShipment: async (values: CreateStagedShipmentInput): Promise<any> => {
    try {
      return await API.graphql({
        query: createStagedShipmentMutation,
        variables: {
          destination: values.destination,
          shipmentType: values.shipmentType,
          plannedShipmentDate: values.plannedShipmentDate,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  listAliquotLocations: async (searchTerm: string): Promise<any> => {
    try {
      return await API.graphql({
        query: listAliquotLocationsQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },

  getStagedShipmentMeta: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: getStagedShipmentMetaQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },

  getStagedShipment: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: getStagedShipmentQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },

  getRankedCryoboxes: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: getRankedCryoboxesQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },

  addCryoboxToStaging: async (values: AddCryoboxToStagingInput): Promise<any> => {
    try {
      return await API.graphql({
        query: addCryoboxToStagingMutation,
        variables: {
          cryoboxBarcode: values.cryoboxBarcode,
          cryoboxUuid: values.cryoboxUuid,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  removeCryoboxFromStaging: async (values: string): Promise<any> => {
    try {
      return await API.graphql({
        query: removeCryoboxFromStagingMutation,
        variables: {
          cryoboxUuid: values,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  resetStagedShipment: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: resetStagedShipmentMutation,
        variables: {},
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },
  getStagingPickList: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: getStagingPickListQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },
  moveStagedAliquot: async (values: MoveStagedAliquotInput): Promise<any> => {
    try {
      return await API.graphql({
        query: moveStagedAliquotMutation,
        variables: {
          aliquotUuid: values.aliquotUuid,
          updatedCryoboxBarcode: values.updatedCryoboxBarcode,
          updatedCryoboxPosition: values.updatedCryoboxPosition,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  manifestStagedShipment: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: manifestStagedShipmentMutation,
        variables: {},
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  listShipments: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: listShipmentsQuery,
        variables: {},
      });
    } catch (error) {
      return error;
    }
  },

  getShipment: async (id: string): Promise<any> => {
    try {
      return await API.graphql({
        query: getShipmentQuery,
        variables: {
          shipmentId: id,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  getShipmentManifest: async (shipmentId: string): Promise<any> => {
    try {
      return await API.graphql({
        query: getShipmentManifestQuery,
        variables: {
          shipmentId: shipmentId,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  finaliseShipment: async (values: FinaliseShipmentInput): Promise<any> => {
    try {
      return await API.graphql({
        query: finaliseShipmentMutation,
        variables: {
          shipmentId: values.shipmentId,
          shippedDate: values.shippedDate,
        },
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },

  shipmentConfirmQC: async (): Promise<any> => {
    try {
      return await API.graphql({
        query: shipmentConfirmQCMutation,
        variables: {},
      });
    } catch (error) {
      console.log(error);
      return error;
    }
  },
};

export default participantApi;
